import { createFileRoute } from '@tanstack/react-router'
import { useState} from 'react'
import {PaymentStatus, PaymentType} from '@/types'
import clsx from 'clsx'
import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card'
import {Banknote, Check, ClockIcon, IdCardIcon, Shield, X} from 'lucide-react'
import UserFilter from '@/components/user-filter'
import { DataTable } from '@/components/ui/data-table'
import type { PaginationState } from '@tanstack/react-table'
import NumberWithHoverPoints from '@/components/NumberWithHoverPoint'
import BankIcons from '@/components/bank-icons'

export const Route = createFileRoute('/client/payment/')({
  component: ClientPayment,
})

const getBankInfo = (value: unknown) => {
  if (!value) return undefined

  if (typeof value === 'string') {
    try {
      return JSON.parse(value)
    } catch {
      return undefined
    }
  }

  return value as Record<string, any>
}

const getCardNumber = (bankInfo: unknown) => {
  const parsedBankInfo = getBankInfo(bankInfo)
  return parsedBankInfo?.SecurePan || parsedBankInfo?.CardHolderPan
}

const formatCardNumber = (cardNumber: string) =>
  cardNumber.match(/.{1,4}/g)?.join(' ') || cardNumber

const PaymentStatusContent = ({status}: {status: keyof typeof PaymentStatus}) => (
  <div
    className={clsx(
      'flex items-center gap-1 text-xs',
      status === 'paid'
        ? 'text-green-600 dark:text-green-400'
        : status === 'unpaid'
          ? 'text-blue-600 dark:text-blue-400'
          : 'text-red-600 dark:text-red-400'
    )}
  >
    {status === 'paid' ? (
      <Check className='size-3.5 shrink-0' />
    ) : status === 'canceled' || status === 'reject' ? (
      <X className='size-3.5 shrink-0' />
    ) : (
      <ClockIcon className='size-3.5 shrink-0' />
    )}
    <span>{PaymentStatus[status] || '-'}</span>
  </div>
)

const PaymentTypeContent = ({type}: {type: keyof typeof PaymentType}) => (
  <div className='flex items-center gap-1 text-xs'>
    {type === 'online' ? (
      <Banknote className='size-3.5 shrink-0' />
    ) : type === 'increase_admin' ? (
      <Shield className='size-3.5 shrink-0' />
    ) : (
      <IdCardIcon className='size-3.5 shrink-0' />
    )}
    <span>{PaymentType[type] || '-'}</span>
  </div>
)

function ClientPayment() {
  const [filtered, setFiltered] = useState<any>(null)
  const [meta, setMeta] = useState<any>()
  const [pagination, setPagination] = useState<PaginationState>({pageIndex: 0, pageSize: 10})
  

  const columns:any = [
    {
      header: 'پرداخت',
      id: 'payment',
      cell: ({row}: any) => {
        const payment = row.original
        return (
          <div className='space-y-1'>
            <div className='flex items-center gap-1.5'>
              <BankIcons bankName={payment.bank_name} isColorFull />
              <PaymentTypeContent type={payment.type} />
            </div>
            <PaymentStatusContent status={payment.status} />
          </div>
        )
      },
    },
    {
      header: 'مبالغ',
      id: 'amounts',
      cell: ({row}: any) => {
        const payment = row.original
        return (
          <div className='space-y-1 text-xs'>
            <div>
              <div className='text-muted-foreground'>واریز</div>
              <NumberWithHoverPoints number={Number(payment.price)} />
            </div>
            <div>
              <div className='text-muted-foreground'>موجودی بعد از تراکنش</div>
              <NumberWithHoverPoints number={Number(payment.wallet_value_after_transaction)} />
            </div>
          </div>
        )
      },
    },
    {
      header: 'شناسه‌ها',
      id: 'identifiers',
      cell: ({row}: any) => {
        const payment = row.original
        const cardNumber = getCardNumber(payment.bank_info)

        return (
          <div className='space-y-1.5 text-xs'>
            <div>
              <div className='text-muted-foreground'>شماره سند</div>
              <div dir='ltr' className='break-all text-right tabular-nums'>
                {payment.resnumber || '-'}
              </div>
            </div>
            {cardNumber && (
              <div>
                <div className='flex items-center gap-1 text-muted-foreground'>
                  <BankIcons cardNumber={cardNumber} isColorFull />
                  <span>شماره کارت</span>
                </div>
                <div dir='ltr' className='break-words text-right text-[11px] tabular-nums'>
                  {formatCardNumber(cardNumber)}
                </div>
              </div>
            )}
          </div>
        )
      },
    },
    {
      header: 'تاریخ',
      accessorKey: 'created_at',
      cell({getValue}: {getValue: () => string}) {
        const date = new Date(getValue())

        return (
          <div dir='ltr' className='text-right text-xs text-muted-foreground'>
            <div>{Intl.DateTimeFormat('fa-ir', {dateStyle: 'short'}).format(date)}</div>
            <div>{Intl.DateTimeFormat('fa-ir', {timeStyle: 'short'}).format(date)}</div>
          </div>
        )
      },
    },
  ]

  return (
    <>
      <Card>
        <CardHeader className='p-3 lg:p-6'>
          <CardTitle className='text-base lg:text-2xl'>گزارش افزایش اعتبار</CardTitle>
        </CardHeader>

        <CardContent className='p-2 pt-0 lg:p-6 lg:pt-0'>
          <UserFilter
            table='payment'
            filtered={filtered}
            setFiltered={setFiltered}
            setMeta={setMeta}
            filters={['resnumber']}
            page={pagination.pageIndex}
            perPage={pagination.pageSize}
          />
          {filtered && (
            <div className='[&_table]:table-fixed [&_th]:h-10 [&_th]:px-2 [&_td]:p-2'>
              <DataTable
                columns={columns}
                data={filtered}
                pageCount={meta.last_page}
                pagination={pagination}
                onPaginationChange={setPagination}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </>
  )
}
